import React from "react";
import moment from "moment";
import { Checkbox } from "@/global/base/checkbox.tsx";
import { DownloadButton } from "@/global/base/download-button";
import ComplianceIndicator from "../../common/ViewPAF/components/ComplianceIndicator";
import { resolveComplianceIndicator } from "../../common/ViewPAF/utils/prescriptionComplianceHelpers";
import Info from "../../../assets/icons/info.svg";
/**
 * Builds select options from API filter_data: { value: id, label: "Name (total)" }.
 * @param {Array} list - filter_data[key] e.g. [{ id, value, total }, ...]
 * @returns {Array} { value, label } for DataTable select
 */
function optionsFromFilterData(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  return list.map((item) => ({
    value: String(item.id),
    label: `${item.value} (${item.total})`,
  }));
}

export function hasReviewedDateColumn(patients = []) {
  return patients.some(
    (row) => row?.prescription && "reviewed_date" in row.prescription
  );
}

function formatReviewedDate(iso) {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("DD-MMM-YYYY").toUpperCase() : "—";
}

const reviewedDateColumn = {
  id: "reviewed_date",
  accessorFn: (row) => row?.prescription?.reviewed_date,
  header: "Reviewed Date",
  cell: ({ row }) => (
    <span className="text-align-center font-size-16">
      {formatReviewedDate(row.original?.prescription?.reviewed_date)}
    </span>
  ),
  meta: { disableFilterMenu: true, align: "left" },
};


/**
 * Column definitions for the global DataTable on View Patients.
 * @param {Function} onExportPDF - (e, patientId) => void
 * @param {Object} filterData - res.data.filter_data from /prescriptions (indication, product, gender, status)
 * @param {Array} patients - current table rows; used to show Reviewed Date only when present
 * @returns {Array} columns for DataTable
 */
export function getViewPatientsColumns(onExportPDF, filterData = {}, patients = []) {
  const genderOptions = optionsFromFilterData(filterData.gender);
  const statusOptions = optionsFromFilterData(filterData.status);
  const indicationOptions = optionsFromFilterData(filterData.indication);
  const productOptions = optionsFromFilterData(filterData.product);
  const showReviewedDate = hasReviewedDateColumn(patients);

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          data-testid="view-patients-select-all"
        />
      ),
      cell: ({ row }) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={`Select row ${row.original?.id}`}
            data-testid={`view-patients-row-select-${row.original?.id}`}
          />
        </div>
      ),
      meta: { disableFilterMenu: true },
      size: 40,
    },
    {
      id: "id",
      accessorKey: "id",
      header: "Patient ID",
      cell: ({ row }) => (
        <span className="text-wrap text-align-center font-size-16">
          {row.original?.id ?? "—"}
        </span>
      ),
      meta: { align: "left", filterName: "id" },
    },
    {
      id: "prescriber_name",
      accessorFn: (row) => row?.prescriber?.full_name,
      header: "Prescriber",
      cell: ({ row }) => {
        const name = row.original?.prescriber?.full_name || "—";
        const display = name.length > 30 ? name.substring(0, 30) + "..." : name;
        return (
          <div className="tooltip-status">
            <div className="font-size-16">{display}</div>
            <span className="tooltiptext-status">{name}</span>
          </div>
        );
      },
      meta: { align: "left" },
    },
    {
      id: "patient_initials",
      accessorKey: "patient_initials",
      header: "Initials",
      cell: ({ row }) => (
        <span className="text-wrap text-align-center font-size-16">
          {row.original?.patient_initials ?? "—"}
        </span>
      ),
      meta: { align: "left" },
    },
    {
      id: "gender",
      accessorKey: "gender",
      header: "Gender",
      cell: ({ row }) => {
        const g = row.original?.gender;
        const label = g === "m" ? "Male" : g === "f" ? "Female" : g ?? "—";
        return (
          <span className="text-wrap text-align-center font-size-16">
            {label}
          </span>
        );
      },
      meta: {
        align: "left",
        filterName: "gender",
        type: "select",
        options: genderOptions,
      },
    },
    {
      id: "dob",
      accessorKey: "dob",
      header: "DOB",
      cell: ({ row }) => (
        <span className="text-align-center font-size-16">
          {row.original?.dob
            ? moment(row.original.dob).format("DD / MM / YYYY")
            : "—"}
        </span>
      ),
      meta: {
        type: "dateRange", align: "left",
        filterNameStart: 'dob_after',
        filterNameEnd: 'dob_before'
      },
    },
    {
      id: "indication",
      accessorFn: (row) => {
        const ind = row?.prescription?.indication;
        if (ind?.includes("Other"))
          return `Other (${row?.prescription?.indication_other || ""})`;
        if (ind?.includes("Clinical "))
          return `Clinical (${row?.prescription?.clinical_trial || ""})`;
        return ind || "—";
      },
      header: "Indication",
      cell: ({ row }) => {
        const ind = row.original?.prescription?.indication;
        const other = row.original?.prescription?.indication_other;
        const trial = row.original?.prescription?.clinical_trial;
        let val = "—";
        if (ind?.includes("Other")) val = `Other (${other || ""})`;
        else if (ind?.includes("Clinical ")) val = `Clinical (${trial || ""})`;
        else if (ind) val = ind;
        return (
          <span className="text-wrap text-align-center font-size-16">{val}</span>
        );
      },
      meta: {
        align: "left",
        filterName: "indication",
        type: "select",
        options: indicationOptions,
      },
    },
    {
      id: "product",
      accessorFn: (row) => row?.prescription?.product ?? "—",
      header: "Molecule",
      cell: ({ row }) => (
        <span className="text-wrap text-align-center font-size-16">
          {row.original?.prescription?.product ?? "—"}
        </span>
      ),
      meta: { align: "left", filterName: "product", type: "select", options: productOptions },
    },
    {
      id: "status",
      accessorFn: (row) => row?.prescription?.status ?? "—",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original?.prescription?.status;
        const reason = row.original?.prescription?.rejection_reason;
        const reasonOther = row.original?.prescription?.rejection_reason_other;
        const isRejected = status === "Rejected";
        const isRed =
          status === "Rejected" ||
          status === "Status Update" ||
          status === "Overdue" ||
          status === "Expiring";
        return (
          <div className="status-cell-with-tooltip">
            <span
              className={
                isRed ? "red font-size-16" : "font-size-16 mine-shaft"
              }
            >
              {status ?? "—"}
            </span>
            {isRejected && (
              <div
                className="tooltip-status"
                data-testid={`view-patients-row-info-${row.original?.id}`}
              >
                <img src={Info} alt="" className="info-icon" />
                <span className="tooltiptext-status">
                  {String(reason).toLowerCase() ===
                    "other (please state)".toLowerCase()
                    ? reasonOther
                    : reason}
                </span>
              </div>
            )}
          </div>
        );
      },
      meta: {
        align: "left",
        filterName: "status",
        type: "select",
        options: statusOptions,
      },
    },
    {
      id: "compliance_indicator",
      accessorFn: (row) => row?.prescription?.compliance_indicator,
      header: "Compliance Indicator",
      cell: ({ row }) => {
        const prescription = row.original?.prescription;
        if (!prescription) {
          return <span className="font-size-16">—</span>;
        }
        const value = resolveComplianceIndicator(prescription);
        if (value !== true && value !== false) {
          return <span className="font-size-16">—</span>;
        }
        return (
          <div className="flex items-center justify-center">
            <ComplianceIndicator value={value} showLabel />
          </div>
        );
      },
      meta: { disableFilterMenu: true, align: "center" },
    },
  ];

  if (showReviewedDate) {
    columns.push(reviewedDateColumn);
  }

  columns.push({
      id: "pdf",
      header: "PDF",
      cell: ({ row }) =>
        row.original?.prescription ? (
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <DownloadButton
              onDownload={(e) => onExportPDF?.(e, row.original?.id)}
              className="p-1 h-8"
              defaultWidth={100}
              aria-label={`Download PDF for patient ${row.original?.id ?? ""}`}
              data-testid={`view-patients-row-download-${row.original?.id}`}
            />
          </div>
        ) : (
          <span className="text-align-right pdf">—</span>
        ),
      meta: { disableFilterMenu: true, align: "center" },
    });

  return columns;
}
